import { useEffect, useState } from "react";
import { Anchor, ActionIcon, Group, Image, Modal, Table, Text } from "@mantine/core";
import { IconDownload, IconX } from "@tabler/icons-react";
import mammoth from "mammoth";
import * as XLSX from "xlsx";
import { renderAsync } from "docx-preview";
import { useRef } from "react";
import {
  fetchFileArrayBuffer,
  fetchFileText,
  fetchSignedFileUrl,
  getFileCategory,
  getFileName,
  getTokenFromCookie,
} from "../utils/fileStorage";

function parseCsvText(content) {
  return content
    .split(/\r?\n/)
    .filter(Boolean)
    .map((line) => line.split(","));
}

function PreviewTable({ rows }) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return (
      <Table withBorder withColumnBorders highlightOnHover>
        <tbody>
          <tr><td>No data found.</td></tr>
        </tbody>
      </Table>
    );
  }

  const header = Array.isArray(rows[0]) ? rows[0] : [];
  const bodyRows = rows.slice(1);

  return (
    <Table
      withBorder
      withColumnBorders
      highlightOnHover
      style={{
        border: "1px solid #d7e7e1",
        backgroundColor: "#ffffff",
      }}
    >
      <thead style={{ backgroundColor: "#d9f7ef" }}>
        <tr>
          {header.map((cell, index) => (
            <th
              key={index}
              style={{
                padding: "10px 12px",
                textAlign: "left",
                fontWeight: 700,
                color: "#0b3d2e",
                borderBottom: "1px solid #bfe9dc",
                whiteSpace: "nowrap",
              }}
            >
              {cell?.toString?.() || ""}
            </th>
          ))}
        </tr>
      </thead>
      <tbody style={{ backgroundColor: "#ffffff" }}>
        {bodyRows.length === 0 ? (
          <tr><td>No data found.</td></tr>
        ) : (
          bodyRows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {(Array.isArray(row) ? row : []).map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  style={{
                    padding: "10px 12px",
                    textAlign: "left",
                    color: "#1b1b1b",
                    borderBottom: "1px solid #eef5f2",
                    backgroundColor: "#ffffff",
                  }}
                >
                  {cell?.toString?.() || ""}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </Table>
  );
}

export default function FilePreviewModal({ file, opened, onClose, shareToken }) {
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [previewTable, setPreviewTable] = useState([]);
  const [previewHtml, setPreviewHtml] = useState("");
  const docxContainerRef = useRef(null);
    
  const handleDownload = async () => {
    try {
      const token = getTokenFromCookie();
      const signedUrl = await fetchSignedFileUrl({ file, shareToken, token });
      const downloadUrl = signedUrl.includes("?")
        ? `${signedUrl}&download=1`
        : `${signedUrl}?download=1`;
      const response = await fetch(downloadUrl, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to download file");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = getFileName(file) || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };
  useEffect(() => {
    if (!opened || !file) {
      setPreviewUrl("");
      setPreviewLoading(false);
      setPreviewError("");
      setPreviewText("");
      setPreviewHtml("");
      setPreviewTable([]);
      return;
    }

    const token = getTokenFromCookie();
    const category = getFileCategory(file);

    async function loadPreview() {
      try {
        setPreviewLoading(true);
        setPreviewError("");
        setPreviewText("");
        setPreviewHtml("");
        setPreviewTable([]);
        setPreviewUrl("");

        if (category === "docx") {
          const arrayBuffer = await fetchFileArrayBuffer({ file, shareToken, token });
          setPreviewLoading(false); // render happens after the DOM node exists
          requestAnimationFrame(async () => {
            if (docxContainerRef.current) {
              docxContainerRef.current.innerHTML = "";
              await renderAsync(arrayBuffer, docxContainerRef.current, undefined, {
                inWrapper: true,
                ignoreWidth: true,
                ignoreHeight: true,
              });
            }
          });
          return;
        }

        if (category === "xlsx") {
          const arrayBuffer = await fetchFileArrayBuffer({ file, shareToken, token });
          const workbook = XLSX.read(arrayBuffer, { type: "array" });
          const firstSheetName = workbook.SheetNames[0];
          const rows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], { header: 1 });
          setPreviewTable(rows);
          return;
        }

        if (category === "csv") {
          const content = await fetchFileText({ file, shareToken, token });
          setPreviewTable(parseCsvText(content));
          return;
        }

        const signedUrl = await fetchSignedFileUrl({ file, shareToken, token, download: false });

      if (category === "pdf" || category === "video" || category === "audio") {
        const authToken = getTokenFromCookie();
        const response = await fetch(signedUrl, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPreviewUrl(blobUrl);
    } else {
        setPreviewUrl(signedUrl);
    }
      } catch (error) {
        console.error("Preview failed:", error);
        setPreviewError(error.message || "Failed to preview file");
      } finally {
        setPreviewLoading(false);
      }
    }

    loadPreview();
  }, [file, opened, shareToken]);

  const category = getFileCategory(file);

  return (
  <Modal
    opened={opened}
    onClose={onClose}
    centered
    size={category === "docx" ? "1100px" : "xl"}
    overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
    withCloseButton={false}
    title={null}
  >
    <Group justify="space-between" mb="md" align="center">
      <Text fw={500} size="sm">{getFileName(file)}</Text>
      <Group gap={4}>
        <ActionIcon 
          size="sm" 
          variant="transparent" 
          onClick={handleDownload} 
          title="Download"
          style={{ 
            border: "none", 
            background: "none", 
            boxShadow: "none",
            color: "gray",
            outline: "none",
          }}
          styles={{
            root: {
              '&:hover': {
                background: 'none !important',
                border: 'none !important',
              }
            }
          }}
        >
          <IconDownload size={16} color="gray" />
        </ActionIcon>
        <ActionIcon 
          size="sm" 
          variant="transparent" 
          onClick={onClose} 
          title="Close"
          style={{ 
            border: "none", 
            background: "none", 
            boxShadow: "none",
            color: "gray",
            outline: "none",
          }}
          styles={{
            root: {
              '&:hover': {
                background: 'none !important',
                border: 'none !important',
              }
            }
          }}
        >
          <IconX size={16} color="gray" />
        </ActionIcon>
      </Group>
    </Group>

      {previewLoading && <Text size="sm">Loading preview...</Text>}
      {previewError && <Text size="sm" c="red">{previewError}</Text>}

      {!previewLoading && !previewError && category === "image" && previewUrl && (
        <Image src={previewUrl} alt={getFileName(file)} fit="contain" />
      )}

      {!previewLoading && !previewError && category === "audio" && previewUrl && (
        <audio controls style={{ width: "100%" }} src={previewUrl} />
      )}

      {!previewLoading && !previewError && category === "video" && previewUrl && (
        <video controls style={{ width: "100%" }} src={previewUrl} />
      )}

      {!previewLoading && !previewError && category === "pdf" && previewUrl && (
        <iframe
          title={getFileName(file)}
          src={previewUrl}
          style={{ width: "100%", height: "70vh", border: "none" }}
        />
      )}

      {!previewLoading && !previewError && category === "docx" && (
        <div
          style={{
            maxHeight: "70vh",
            overflowY: "auto",
            overflowX: "hidden",
            backgroundColor: "#f5f5f5",
            borderRadius: 6,
            padding: "8px",
          }}
        >
      <style>{`
        .docx-wrapper {
          background: transparent !important;
          padding: 0 !important;
        }
        .docx-wrapper > section.docx {
          margin: 0 auto !important;
          box-shadow: 0 1px 6px rgba(0,0,0,0.12) !important;
        }
      `}</style>
      <div ref={docxContainerRef} style={{ maxWidth: "100%" }} />
    </div>
  )}

      {!previewLoading && !previewError && (category === "xlsx" || category === "csv") && (
        <div style={{ maxHeight: "70vh", overflow: "auto" }}>
          <PreviewTable rows={previewTable} />
        </div>
      )}

      {!previewLoading && !previewError && category === "other" && previewUrl && (
        <div>
          <Text size="sm" mb="xs">
            Preview is not available for this file type.
          </Text>
          <Anchor href={previewUrl} target="_blank" rel="noopener noreferrer">
            Download {getFileName(file)}
          </Anchor>
        </div>
      )}
    </Modal>
  );
}